import React, { useState, useEffect } from 'react';
import { StrategyOption } from '../../types';
import { StrategyCard } from './StrategyCard';
import { Box, Typography, Button, Chip, Alert } from '@mui/material';
import { Bolt as ZapIcon, ArrowForward as ArrowRightIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';

interface StrategySelectionViewProps {
    options: StrategyOption[];
    onConfirm: (strategyId: string) => void;
}

export const StrategySelectionView: React.FC<StrategySelectionViewProps> = ({
    options,
    onConfirm
}) => {
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Auto-select recommended strategy on mount
    useEffect(() => {
        const recommended = options.find(opt => opt.is_recommended);
        if (recommended) {
            setSelectedId(recommended.id);
        }
    }, [options]);

    const mostlyFlatCards = options.filter((opt) => {
        const states = opt.impact_metric_states || [];
        if (states.length === 0) return false;
        const noChange = states.filter((s) => s.status === 'no_material_change').length;
        const computed = states.filter((s) => s.status === 'computed').length;
        return noChange >= 2 && computed <= 1;
    }).length;
    const showSharedConvergenceNote = options.length >= 3 && mostlyFlatCards >= Math.min(3, options.length);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Header Section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Chip
                    icon={<ZapIcon sx={{ fontSize: 14 }} />}
                    label="Strategic Intent Phase"
                    sx={{
                        mb: 2,
                        height: 28,
                        px: 1,
                        bgcolor: 'rgba(245, 158, 11, 0.1)',
                        color: '#d97706',
                        border: '1px solid rgba(245, 158, 11, 0.25)',
                        '& .MuiChip-icon': { color: '#f59e0b' },
                        '& .MuiChip-label': {
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                        },
                    }}
                />
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 800,
                        color: '#0f172a',
                        mb: 1,
                        fontSize: { xs: '1.25rem', md: '1.75rem' },
                        letterSpacing: '-0.02em',
                    }}
                >
                    Select Allocation Strategy
                </Typography>
                <Typography
                    sx={{
                        color: '#64748b',
                        maxWidth: '480px',
                        mx: 'auto',
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        fontWeight: 500,
                    }}
                >
                    Choose a directive to guide downstream optimization based on synthesized market intelligence.
                </Typography>
            </Box>

            {showSharedConvergenceNote && (
                <Alert
                    icon={<InfoIcon fontSize="inherit" />}
                    severity="info"
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        '& .MuiAlert-message': { width: '100%' },
                    }}
                >
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155', mb: 0.25 }}>
                        Low-sensitivity preflight preview
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>
                        Most strategies project similar outcomes on the preview metrics for this scenario. Strategy choice can still shape downstream behavior, but expected KPI deltas look small at this stage.
                    </Typography>
                </Alert>
            )}

            {/* Strategy Cards Grid */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                    gap: 2.5,
                    mb: 4,
                }}
            >
                {options.map((option) => (
                    <StrategyCard
                        key={option.id}
                        strategy={option}
                        isSelected={selectedId === option.id}
                        onSelect={setSelectedId}
                    />
                ))}
            </Box>

            {/* In-Flow Action Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 4 }}>
                <Button
                    variant="contained"
                    disabled={!selectedId}
                    onClick={() => selectedId && onConfirm(selectedId)}
                    endIcon={<ArrowRightIcon />}
                    sx={{
                        px: 6,
                        py: 1.75,
                        borderRadius: 3,
                        fontSize: '0.9375rem',
                        fontWeight: 700,
                        textTransform: 'none',
                        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                        boxShadow: '0 8px 24px -6px rgba(15, 23, 42, 0.25)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                            boxShadow: '0 12px 32px -8px rgba(15, 23, 42, 0.35)',
                            transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                            background: '#e2e8f0',
                            color: '#94a3b8',
                            boxShadow: 'none',
                        },
                        transition: 'all 0.25s ease',
                    }}
                >
                    Inject Strategic Intent
                </Button>
            </Box>
        </Box>
    );
};
